import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, Calendar, Building2, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EducationEntry } from '@/types/jobWizard';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';

interface JobEducationTimelineProps {
  education: EducationEntry[];
}

const JobEducationTimeline: React.FC<JobEducationTimelineProps> = ({ education }) => {
  if (!education || education.length === 0) return null;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return format(parseISO(dateStr), 'MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const sortedEducation = [...education].sort((a, b) => {
    const dateA = a.endDate || a.startDate || '';
    const dateB = b.endDate || b.startDate || '';
    return dateB.localeCompare(dateA);
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GraduationCap className="w-4 h-4 text-primary" />
          Education
          <Badge variant="secondary" className="ml-auto text-xs">
            {education.length} {education.length === 1 ? 'Entry' : 'Entries'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary/50 via-primary/20 to-transparent" />
          
          <div className="space-y-6">
            {sortedEducation.map((entry, index) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative pl-10"
              >
                {/* Timeline dot */}
                <div className={cn(
                  "absolute left-2 top-1 w-5 h-5 rounded-full border-2 flex items-center justify-center",
                  entry.isCurrent 
                    ? "bg-emerald-500 border-emerald-300 dark:border-emerald-700" 
                    : "bg-background border-primary"
                )}>
                  {entry.isCurrent ? (
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  ) : (
                    <GraduationCap className="w-2.5 h-2.5 text-primary" />
                  )}
                </div>

                {/* Education Card */}
                <div className={cn(
                  "p-4 rounded-xl border transition-all hover:shadow-md",
                  entry.isCurrent 
                    ? "bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200 dark:from-emerald-900/20 dark:to-green-900/10 dark:border-emerald-700/40"
                    : "bg-card border-border hover:border-primary/30"
                )}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{entry.degree}</h4>
                      <div className="flex items-center gap-1.5 text-muted-foreground mt-0.5">
                        <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="text-sm truncate">{entry.institution}</span>
                      </div>
                    </div>
                    
                    {entry.isCurrent && (
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 flex-shrink-0">
                        Current
                      </Badge>
                    )}
                  </div>

                  {entry.fieldOfStudy && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
                      <BookOpen className="w-3 h-3" />
                      <span>{entry.fieldOfStudy}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
                    <Calendar className="w-3 h-3" />
                    <span>
                      {formatDate(entry.startDate)}
                      {(entry.endDate || entry.isCurrent) && ' — '}
                      {entry.isCurrent ? 'Present' : formatDate(entry.endDate)}
                    </span>
                  </div>

                  {entry.description && (
                    <p className="text-xs text-muted-foreground mt-3 leading-relaxed line-clamp-3">
                      {entry.description}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default JobEducationTimeline;
